/**
 * @file draft-po-details.tsx
 * @module app/modules/uProcure/po-management/draft-po-details
 * @description One draft PO: supplier, products (quantity in the last PO's quantity type, cost
 *   price, the ATS figures behind the suggested quantity) and the full timeline of who did what.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Card, Spinner } from "react-bootstrap";
import APICallService from "../../../../api/apiCallService";
import { PURCHASEORDER } from "../../../../api/apiEndPoints";
import { POManagement } from "../../../../utils/constants";
import Method from "../../../../utils/methods";
import { useWarehouse } from "../../../context/WarehouseContext";
import { DeleteDraftPoModal } from "./draft-po-list";
import DraftPoTimeline from "./draft-po-timeline";
import { DraftPoDetail, DraftLine } from "./draft-po.types";
import { DraftPoEditor, SubmitDraftPoModal } from "./draft-po-editor";
import { success } from "../../../../Global/toast";
import { DraftGroupBadge } from "./draft-po-group";
import DraftCreditNote from "./draft-po-credit";

const QTY_TYPES: Record<number, string> = {
  1: "Carton with Dozens",
  2: "Dozen with Pieces",
  3: "Piece",
  4: "Carton with Pieces",
};
const ACTIONS: Record<string, string> = {
  created: "Draft created",
  product_added: "Product added",
  product_removed: "Product removed",
  quantity_changed: "Quantity changed",
  supplier_changed: "Supplier changed",
  submitted: "Submitted as Pending PO",
  deleted: "Draft deleted",
};
const num = (n: number | null | undefined, d = 0) =>
  n === null || n === undefined ? "-" : Number(n).toLocaleString("en-US", { maximumFractionDigits: d });

const DraftPoDetails = () => {
  const location = useLocation();
  const state = location.state as { id?: string } | null;
  const navigate = useNavigate();
  const { selectedWarehouse } = useWarehouse();
  const [draft, setDraft] = useState<DraftPoDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const isOpen = draft?.status === "draft";
  // blocked by tonnage / MOQ unless this user may bypass every rule that is not met
  const rules = draft?.rules;
  const bypassAllowed =
    !rules ||
    rules.ok ||
    ((!rules.belowTonnage || Boolean(draft?.canBypass?.tonnage)) &&
      (rules.belowMoq.length === 0 || Boolean(draft?.canBypass?.moq)) &&
      ((rules.oldPendingPos ?? []).length === 0 || Boolean(draft?.canBypass?.oldPendingPo)));
  const oldPending = rules?.oldPendingPos ?? [];

  useEffect(() => {
    if (!state?.id) {
      navigate("/UProcure/purchase-order-management");
      return;
    }
    (async () => {
      const api = new APICallService(
        PURCHASEORDER.draftPurchaseOrderDetails,
        state.id,
        "",
        "",
        "",
        "",
        POManagement,
        selectedWarehouse?.value
      );
      const res = await api.callAPI();
      if (res && res.refKey) setDraft(res);
      setLoading(false);
    })();
  }, [state?.id]);

  if (loading) {
    return (
      <div className="text-center py-20">
        <Spinner animation="border" />
      </div>
    );
  }
  if (!draft) {
    return <div className="text-center py-20 text-gray-600">Draft PO not found</div>;
  }

  return (
    <div className="layout-padding">
      <div className="d-flex align-items-center justify-content-between mb-6">
        <div>
          <h1 className="fs-22 fw-bolder mb-1">
            Draft PO {draft.refKey}{" "}
            <span className={`badge badge-light-${draft.source === "auto" ? "primary" : "info"} fs-13 align-middle`}>
              {draft.source === "auto" ? "Automatic (Replenishment report)" : "Manual"}
            </span>
          </h1>
          <div className="fs-15 text-gray-700">
            Supplier:{" "}
            {draft.business ? (
              <>
                <span className="fw-bold">{draft.business.name}</span>
                <DraftGroupBadge draft={draft} />
              </>
            ) : (
              <span className="badge badge-light-danger">No supplier (choose one when editing)</span>
            )}
            {"  ·  "}Warehouse: <span className="fw-bold">{draft.warehouse?.name}</span>
            {"  ·  "}Value: <span className="fw-bold">TSh {num(draft.totalValue)}</span>
            {"  ·  "}Weight: <span className="fw-bold">{num(draft.totalWeightKg / 1000, 2)} t</span>
            {draft.tonnageTon ? (
              <span className={draft.totalWeightKg / 1000 >= draft.tonnageTon ? "text-success fw-600" : "text-warning fw-600"}>
                {" "}of {draft.tonnageTon} t tonnage
              </span>
            ) : (
              <span className="text-gray-600"> (no supplier tonnage set)</span>
            )}
          </div>
          {isOpen && <DraftCreditNote credit={draft.credit} />}
        </div>
        <div className="d-flex gap-2">
          {isOpen && draft.canEdit && !editing && (
            <>
              <Button className="uw-btn uw-btn-primary" onClick={() => setEditing(true)}>
                Edit Draft
              </Button>
              <Button
                className="uw-btn uw-btn-primary"
                onClick={() => setSubmitting(true)}
                disabled={!draft.business || !draft.lines.length || !bypassAllowed}
                title={
                  !draft.business
                    ? "Choose a supplier first (Edit Draft)"
                    : !bypassAllowed
                      ? oldPending.length > 0 && !draft.canBypass?.oldPendingPo
                        ? "Products on an old Pending PO: super admin approval needed"
                        : "Below the supplier PO tonnage / MOQ"
                      : ""
                }
              >
                Submit for Approval
              </Button>
            </>
          )}
          {isOpen && draft.canDelete && !editing && (
            <Button variant="light-danger" onClick={() => setConfirmDelete(true)}>
              Delete Draft
            </Button>
          )}
          <Button className="uw-btn uw-btn-secondary" onClick={() => navigate("/UProcure/purchase-order-management")}>
            Back to Purchase Orders
          </Button>
        </div>
      </div>
      {draft.status === "deleted" && draft.deleted && (
        <div className="alert alert-danger fs-14 mb-6">
          <span className="fw-bold">Deleted</span> on {Method.convertDateToDDMMYYYYHHMMAMPM(draft.deleted.at)} by{" "}
          <span className="fw-bold">{draft.deleted.by?.name}</span>. Reason: {draft.deleted.reason}. This draft is not
          used by the automatic job.
        </div>
      )}
      {draft.status === "submitted" && draft.submittedPo && (
        <div className="alert alert-success fs-14 mb-6">
          Submitted as Pending PO <span className="fw-bold">{draft.submittedPo.refKey}</span>.
        </div>
      )}
      <SubmitDraftPoModal
        draft={draft}
        warehouse={selectedWarehouse?.value}
        show={submitting}
        onHide={() => setSubmitting(false)}
        onSubmitted={(po) => {
          setSubmitting(false);
          success(`Pending PO ${po.refKey} created`);
          navigate("/UProcure/purchase-order-management", { state: { tab: 1, refresh: Date.now() } });
        }}
      />
      {draft.status === "draft" && draft.rules && !draft.rules.ok && (
        <div className="alert alert-warning fs-14 mb-6">
          <div className="fw-bold mb-1">This draft cannot be submitted yet</div>
          {draft.rules.belowTonnage && (
            <div>
              Weight {num(draft.rules.totalWeightKg / 1000, 2)} t of the supplier&apos;s {draft.rules.tonnageTon} t PO tonnage:{" "}
              <b>{num(draft.rules.missingTon, 2)} t missing</b>.
            </div>
          )}
          {draft.rules.belowMoq.length > 0 && (
            <div>
              {draft.rules.belowMoq.length} product(s) below the supplier MOQ:{" "}
              {draft.rules.belowMoq
                .slice(0, 5)
                .map((m) => `${m.title} (${m.quantity} of ${m.moq})`)
                .join(", ")}
              {draft.rules.belowMoq.length > 5 ? "..." : ""}
            </div>
          )}
          {oldPending.length > 0 && (
            <div>
              {oldPending.length} product(s) already on a Pending PO older than {draft.oldPoDays ?? ""} days:{" "}
              {[...new Set(oldPending.map((p) => p.refKey))]
                .map((ref) => {
                  const items = oldPending.filter((p) => p.refKey === ref);
                  const names = items.slice(0, 3).map((p) => p.title).join(", ");
                  return `${ref} (${items[0].ageDays} days): ${names}${items.length > 3 ? ` and ${items.length - 3} more` : ""}`;
                })
                .join("; ")}
              . <b>Needs super admin approval.</b>
            </div>
          )}
          <div className="text-gray-700 mt-1">
            {bypassAllowed
              ? "You may submit it anyway; you will be asked to confirm and it is recorded on the timeline."
              : oldPending.length > 0 && !draft.canBypass?.oldPendingPo
                ? "Only a super admin can approve and submit it (products on an old Pending PO could be ordered twice)."
                : "Only a super admin or someone allowed to submit below tonnage / MOQ can submit it."}
          </div>
        </div>
      )}
      <DeleteDraftPoModal
        draft={confirmDelete ? draft : null}
        warehouse={selectedWarehouse?.value}
        onHide={() => setConfirmDelete(false)}
        onDeleted={() => navigate("/UProcure/purchase-order-management")}
      />

      {editing ? (
        <Card className="border mb-6">
          <Card.Body>
            <DraftPoEditor
              draft={draft}
              warehouse={selectedWarehouse?.value}
              onCancel={() => setEditing(false)}
              onSaved={(updated) => {
                setDraft(updated);
                setEditing(false);
              }}
            />
          </Card.Body>
        </Card>
      ) : (
      <Card className="border mb-6">
        <Card.Body className="p-0">
          <div className="table-responsive">
            <table className="table table-row-bordered align-middle gs-5 gy-3 mb-0 fs-14">
              <thead>
                <tr className="fw-bold text-dark bg-light">
                  <th>Product</th>
                  <th>Qty Type (last PO)</th>
                  <th className="text-end">Quantity</th>
                  <th className="text-end">Suggested</th>
                  <th className="text-end">Cost Price</th>
                  <th className="text-end">Line Value</th>
                  <th className="text-end">Run Rate / Day</th>
                  <th className="text-end">ATS</th>
                  <th className="text-end">Open PO</th>
                  <th className="text-end">Lead Time</th>
                  <th className="text-end">DOIH</th>
                  <th className="text-end">MOQ</th>
                  <th>Added</th>
                </tr>
              </thead>
              <tbody>
                {draft.lines.map((l: DraftLine) => (
                  <tr key={l._id}>
                    <td style={{ minWidth: 240 }}>
                      <div className="fw-600">{l.title}</div>
                      {l.refKey && <div className="fs-12 text-gray-600">{l.refKey}</div>}
                    </td>
                    <td className="text-nowrap">{QTY_TYPES[l.quantityType] ?? l.quantityType}</td>
                    <td className="text-end fw-bold">{num(l.quantity)}</td>
                    <td className="text-end">{num(l.suggestedQuantity)}</td>
                    <td className="text-end">
                      {typeof l.costPrice === "number" ? (
                        <>
                          {num(l.costPrice)}
                        </>
                      ) : (
                        <span className="text-danger" title="Set the cost price in the Price Catalog; it shows here automatically">
                          Not in Price Catalog
                        </span>
                      )}
                    </td>
                    <td className="text-end">{typeof l.costPrice === "number" ? num(l.costPrice * l.quantity) : "-"}</td>
                    <td className="text-end">{num(l.ats?.runRate, 2)}</td>
                    <td className="text-end">{num(l.ats?.ats, 2)}</td>
                    <td className="text-end">{num(l.ats?.openPoQty, 2)}</td>
                    <td className="text-end">{l.ats ? `${l.ats.leadTimeDays}d` : "-"}</td>
                    <td className="text-end">{l.ats ? `${l.ats.doih}d` : "-"}</td>
                    <td className="text-end">{l.ats?.moq ? num(l.ats.moq) : "-"}</td>
                    <td className="text-nowrap">
                      {l.addedAt ? Method.convertDateToDDMMYYYYHHMMAMPM(l.addedAt) : "-"}
                      <div className="fs-12 text-gray-600">{l.addedBy?.name}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card.Body>
      </Card>
      )}
      <DraftPoTimeline events={draft.timeline ?? []} />
    </div>
  );
};

export default DraftPoDetails;
